/**
 * Coverage facts for /looker boxes 3, 8 and 9 (spec §9.3). Fetches only for a
 * single workspace and a bounded period; otherwise idle with the reason box 8
 * shows. Same run key as the app boxes and the backfill pill (template + scope
 * + period + language), plus DEC3's Awareness definition (a mapping change
 * refetches), and a stale result is never shown.
 *
 * @module hooks/useLookerCoverage
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadCoverage } from '@/services/looker/lookerCoverageLoader';
import type { LookerCoverage } from '@/services/looker/lookerCoverage';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';
import { logError } from '@/services/logger';
import { payloadRunKey } from './useLookerAppTiles';

export type CoverageState =
  /** Nothing fetched: scope is checked before the period. */
  | { status: 'idle'; reason: 'allWorkspaces' | 'unboundedPeriod' }
  /** Also before the payload arrives: box 8 must not ask for a workspace while the page loads. */
  | { status: 'loading' }
  /**
   * `currency` is the one /looker's server forced (FR-X-02), or, on /overview, D1's page currency when its facts
   * hold spend in more than one (coverageInCurrencyOf). Coverage is NOT filtered by it, and says so.
   * It is not part of the run key, so a currency pick never refetches.
   */
  | { status: 'ready'; data: LookerCoverage; currency?: string }
  /** A read failed: shown as "couldn't load", never as an all clear. */
  | { status: 'failed' };

type Settled = Extract<CoverageState, { status: 'ready' } | { status: 'failed' }>;

export const useLookerCoverage = (payload: LookerDashboardPayload | null): CoverageState => {
  const { i18n } = useTranslation();
  const workspaceId = payload?.scope.kind === 'workspace' ? payload.scope.workspaceId : null;
  const start = payload?.period?.start ?? null;
  const end = payload?.period?.end ?? null;
  const runKey = payload && workspaceId && start && end ? payloadRunKey(payload, i18n.language) : '';
  // DEC3: the Awareness gap follows the mapping; no resultMapping = the central definition.
  const awareness = payload?.resultMapping ? payload.resultMapping.awareness : 'impressions';
  // A result read under the other definition is never shown: the mapping is part of the settle key.
  const settleKey = runKey ? `${runKey}|aw:${String(awareness)}` : '';
  const [result, setResult] = useState<{ key: string; state: Settled } | null>(null);

  useEffect(() => {
    if (!settleKey || !workspaceId || !start || !end) return;
    let ignore = false;
    loadCoverage(workspaceId, { start, end }, awareness).then(
      (data) => {
        if (!ignore) setResult({ key: settleKey, state: { status: 'ready', data } });
      },
      (err: unknown) => {
        logError('LookerCoverage', 'coverage read failed', err);
        if (!ignore) setResult({ key: settleKey, state: { status: 'failed' } });
      },
    );
    return () => {
      ignore = true;
    };
  }, [settleKey, workspaceId, start, end, awareness]);

  const hasPayload = payload !== null;
  const currency = payload?.currency ?? null;
  return useMemo<CoverageState>(() => {
    if (!hasPayload) return { status: 'loading' };
    if (!workspaceId) return { status: 'idle', reason: 'allWorkspaces' };
    if (!runKey) return { status: 'idle', reason: 'unboundedPeriod' };
    if (!result || result.key !== settleKey) return { status: 'loading' };
    return result.state.status === 'ready' && currency ? { ...result.state, currency } : result.state;
  }, [hasPayload, workspaceId, runKey, settleKey, result, currency]);
};
